/**
 * Security configuration and constants
 */
import DOMPurify from 'dompurify';

export const SECURITY_CONFIG = {
  // Rate limiting settings
  RATE_LIMITS: {
    LOGIN: {
      MAX_REQUESTS: parseInt(process.env.REACT_APP_LOGIN_RATE_LIMIT) || 5,
      WINDOW_MS: parseInt(process.env.REACT_APP_LOGIN_RATE_WINDOW) || 15 * 60 * 1000, // 15 minutes
    },
    API: {
      MAX_REQUESTS: parseInt(process.env.REACT_APP_API_RATE_LIMIT) || 100,
      WINDOW_MS: parseInt(process.env.REACT_APP_API_RATE_WINDOW) || 60 * 1000, // 1 minute
    },
    QR_SCAN: {
      MAX_REQUESTS: 50,
      WINDOW_MS: 60 * 1000, // 1 minute
    },
    CHECKOUT: {
      MAX_REQUESTS: 10,
      WINDOW_MS: 60 * 1000, // 1 minute
    }
  },

  // Input validation
  VALIDATION: {
    EMAIL_REGEX: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    PID_REGEX: /^[a-zA-Z0-9]{6,12}$/, // UCF PID format
    MIN_PASSWORD_LENGTH: 6,
    MAX_PASSWORD_LENGTH: 128,
    MAX_NAME_LENGTH: 100,
    MAX_DESCRIPTION_LENGTH: 1000,
    MAX_NOTES_LENGTH: 500
  },

  // Session management
  SESSION: {
    ADMIN_TIMEOUT: 2 * 60 * 60 * 1000, // 2 hours
    GUEST_TIMEOUT: 60 * 60 * 1000, // 1 hour
    MAX_LOGIN_ATTEMPTS: 5,
    LOCKOUT_DURATION: 15 * 60 * 1000 // 15 minutes
  },

  // File upload limits
  UPLOAD: {
    MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
    ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    MAX_FILES_PER_UPLOAD: 10
  },

  // Feature flags
  FEATURES: {
    ENABLE_RATE_LIMITING: process.env.REACT_APP_ENABLE_RATE_LIMITING !== 'false',
    DEBUG_MODE: process.env.REACT_APP_DEBUG_MODE === 'true',
    GUEST_ACCESS: true,
    ADMIN_CREATION: false // Set to true only for initial setup
  },

  // Admin email whitelist - users with these emails automatically get admin role
  ADMIN_EMAILS: [
    'dabbassammy@gmail.com'
    // Add more admin emails here as needed
  ]
};

/**
 * Input sanitization utilities
 */
export const sanitizeInput = {
  // Sanitize HTML content using DOMPurify
  html: (input) => {
    if (typeof input !== 'string') return input;
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                     'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'br', 'div', 'span', 'blockquote', 'pre', 'code'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'style', 'class', 'target', 'rel', 'border'],
      ALLOWED_STYLES: {
        '*': {
          'color': [/.*/],
          'text-align': [/.*/],
          'font-size': [/.*/],
          'font-weight': [/.*/],
          'padding': [/.*/],
          'margin': [/.*/],
          'background-color': [/.*/],
          'border-collapse': [/.*/],
          'width': [/.*/],
          'height': [/.*/],
          'max-width': [/.*/],
          'max-height': [/.*/]
        }
      },
      ALLOW_DATA_ATTR: false
    });
  },

  // Sanitize plain text by stripping all HTML tags
  text: (input) => {
    if (typeof input !== 'string') return input;
    // Use DOMPurify to strip all HTML tags for plain text contexts
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] }).trim();
  },

  // Sanitize URLs to prevent javascript: and data: protocols
  url: (url) => {
    if (typeof url !== 'string') return '';
    const sanitized = url.trim();
    // Block dangerous protocols
    if (/^(javascript|data|vbscript):/i.test(sanitized)) {
      return '';
    }
    return sanitized;
  },

  // Validate email format
  email: (email) => {
    if (typeof email !== 'string') return false;
    return SECURITY_CONFIG.VALIDATION.EMAIL_REGEX.test(email);
  },

  // Validate PID format
  pid: (pid) => {
    if (typeof pid !== 'string') return false;
    return SECURITY_CONFIG.VALIDATION.PID_REGEX.test(pid);
  },

  // Validate password strength
  password: (password) => {
    if (typeof password !== 'string') return false;
    return password.length >= SECURITY_CONFIG.VALIDATION.MIN_PASSWORD_LENGTH &&
           password.length <= SECURITY_CONFIG.VALIDATION.MAX_PASSWORD_LENGTH;
  }
};

/**
 * Security headers and CSP configuration
 */
export const SECURITY_HEADERS = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin'
};

/**
 * Content Security Policy (for production)
 */
export const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'", "https://www.gstatic.com", "https://www.google.com"],
  'style-src': ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com"],
  'font-src': ["'self'", "https://fonts.gstatic.com"],
  'img-src': ["'self'", "data:", "https:", "blob:"],
  'connect-src': ["'self'", "https://*.firebase.com", "https://*.googleapis.com"],
  'media-src': ["'self'", "blob:"],
  'object-src': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"],
  'frame-ancestors': ["'none'"]
};

/**
 * Audit logging utility
 */
export const auditLog = {
  log: (action, userId, details = {}) => {
    if (!SECURITY_CONFIG.FEATURES.DEBUG_MODE) return;
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      action,
      userId,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    console.log('[AUDIT]', logEntry);
    
    // In production, send to logging service
    // sendToLoggingService(logEntry);
  },

  logSecurityEvent: (event, userId, severity = 'medium', details = {}) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'SECURITY_EVENT',
      event,
      userId,
      severity,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href,
      ip: 'client-side' // Will be filled by server
    };
    
    console.warn('[SECURITY]', logEntry);
    
    // In production, send to security monitoring service
    // sendToSecurityService(logEntry);
  }
};
